/**
 * The interface sounds: a click, a ka-ching, a purchase, an error and a pencil
 * (drawn when a raid zone is picked).
 *
 * ─── One delegated listener, not twenty-nine ────────────────────────────────
 * There are 29 click handlers across `base-view/ui/`. Adding a play call to
 * each would be 29 edits, 29 chances to miss one, and a guarantee that the
 * next button added is silent. Instead a single capture-phase listener on the
 * document sees every click, including inside panels that mount outside the
 * view container, and including buttons that do not exist yet.
 *
 * Capture phase specifically: a handler that calls `stopPropagation` would
 * otherwise swallow the sound along with the event.
 *
 * ─── Why some buttons are deliberately silent ───────────────────────────────
 * A vendor purchase is a click AND a buy. Playing both is a double-fire, and
 * a failed purchase playing click-then-error is worse. So buttons whose
 * interaction is owned by a more specific sound are skipped — matched on the
 * `data-action` values the commerce UIs already use (`buy`, `sell`), so this
 * costs no changes to those files. `data-sfx="none"` is honoured too, for
 * anything future that needs to opt out without an action name.
 *
 * ─── Why credits are watched, not hooked ────────────────────────────────────
 * "Every time the player gets ++credits" spans vendor sales, the black-market
 * exchange and quest rewards — three call sites today and an unknown number
 * later. `hud.ts` already subscribes to `economy:updated` globally and writes
 * the result into `gameStateStore`'s `character.softBalance`, so that field is
 * a live, authoritative total that every earning path already flows through.
 * Watching it covers the fourth way to earn money on the day it ships.
 *
 * The cost of watching rather than hooking: a RECONNECT that reconciles a
 * stale sheet shows a balance jump that was not a transaction, and will play
 * one spurious ka-ching. That is a real if uncommon false positive, accepted
 * because the alternative is a sound that silently misses new earning paths.
 */
import type { Store } from '../../core/state/store';
import { gameStateStore } from '../state/game-state';
import { vendorStateStore } from '../state/vendor-state';
import { shopStateStore } from '../state/shop-state';
import { bankerStateStore } from '../state/banker-state';
import { questStateStore } from '../state/quest-state';
import { homeStateStore } from '../state/home-state';
import { arenaStateStore } from '../state/arena-state';
import { currencyShopStateStore } from '../state/currency-shop-state';
import { raidStateStore } from '../state/raid-state';
import type { MixerHandle } from './mixer';
import { createErrorFx } from './error-fx';
import { TRACKS, type TrackId } from './tracks';

/**
 * Buttons the click sound leaves alone, because a more specific sound owns
 * the interaction. Asserted against the real markup in `audio-assets.test.ts`
 * so a renamed action cannot silently reintroduce the double-fire.
 */
export const CLICK_SUPPRESS_SELECTOR = '[data-sfx="none"],[data-action="buy"],[data-action="sell"]';

/**
 * Minimum gap between two plays of the SAME ui sound, ms.
 *
 * Longer than the weapon guard because these are triggered by state rather
 * than by input: two stores erroring in the same tick, or a balance and a
 * receipt landing together, would otherwise double-strike audibly.
 */
const UI_RETRIGGER_MS = 120;

/** Anything with the error field the 15 game stores share. */
type ErrorBearing = Store<{ error: string | null }>;

/**
 * Stores whose `error` going from null to a message is worth a sound.
 *
 * Verified before relying on it: these all reset `error` back to `null` on the
 * next attempt, so the null -> message edge re-arms. A store that left a stale
 * error in place would sound once and then appear broken.
 */
const ERROR_STORES: ErrorBearing[] = [
  vendorStateStore, shopStateStore, bankerStateStore, questStateStore,
  homeStateStore, arenaStateStore, currencyShopStateStore,
] as unknown as ErrorBearing[];

/** Plays `id`, no more often than `UI_RETRIGGER_MS`. */
function playThrottled(mixer: MixerHandle, id: TrackId, last: { at: number }): void {
  if (!mixer.isRunning()) return;
  const now = performance.now();
  if (now - last.at < UI_RETRIGGER_MS) return;
  last.at = now;
  const track = TRACKS[id];
  void mixer.playOnce(track.path, track.channel).catch(() => {
    // Never throw out of a DOM listener or a store subscriber.
  });
}

/** Click on any button, except those a more specific sound owns. */
export function createClickSound(mixer: MixerHandle): () => void {
  const last = { at: -Infinity };
  const onClick = (e: Event): void => {
    const el = e.target as HTMLElement | null;
    if (!el?.closest) return;
    // Buttons and dialogue-tree choices. `closest` rather than a tag check,
    // because a click usually lands on a span inside the button.
    const button = el.closest('button,[role="button"],[data-choice]');
    if (!button) return;
    if (button.closest(CLICK_SUPPRESS_SELECTOR)) return;
    playThrottled(mixer, 'uiClick', last);
  };
  document.addEventListener('click', onClick, { capture: true, passive: true });
  return () => document.removeEventListener('click', onClick, { capture: true });
}

/**
 * When the sell sound last played, so the credits sound can stand down for the
 * same sale. A sale's response lands first and the `economy:updated` push that
 * raises `softBalance` lands later, sometimes outside the 120ms retrigger
 * window; without this the player hears two ka-chings per sale.
 */
const SELL_CREDITS_GRACE_MS = 1500;
const sellSound = { at: -Infinity };

/** Ka-ching whenever the player's cash total goes up. */
export function createCreditsSound(mixer: MixerHandle): () => void {
  const last = { at: -Infinity };
  // `null` means "no sheet seen yet", distinct from a balance of zero.
  let previous: number | null = null;
  return gameStateStore.subscribe((s) => {
    const sheet = s.character;
    if (!sheet || sheet.state !== 'ACTIVE') return;
    const balance = sheet.softBalance;
    if (typeof balance !== 'number') return;
    const before = previous;
    previous = balance;
    // First sheet of the session is the baseline, not an earning.
    if (before === null || balance <= before) return;
    if (performance.now() - sellSound.at < SELL_CREDITS_GRACE_MS) return;
    playThrottled(mixer, 'uiCredits', last);
  });
}

/** A purchase completing at a vendor or in the premium shop. */
export function createPurchaseSound(mixer: MixerHandle): () => void {
  const last = { at: -Infinity };
  // Receipts are replaced by identity on each transaction, so comparing the
  // reference is enough — and is what distinguishes "a new purchase" from
  // "any other field on this store changed".
  let lastVendorReceipt: unknown = vendorStateStore.get().lastReceipt;
  let lastShopReceipt: unknown = shopStateStore.get().lastReceipt;

  const unsubVendor = vendorStateStore.subscribe((s) => {
    const receipt = s.lastReceipt;
    if (receipt === lastVendorReceipt) return;
    lastVendorReceipt = receipt;
    // Only a BUY. A sale is an earning, and the credits sound has it —
    // playing both would double-fire on every sale.
    if (receipt?.kind !== 'buy') return;
    playThrottled(mixer, 'uiBuy', last);
  });

  const unsubShop = shopStateStore.subscribe((s) => {
    const receipt = s.lastReceipt;
    if (receipt === lastShopReceipt) return;
    lastShopReceipt = receipt;
    if (!receipt) return;
    playThrottled(mixer, 'uiBuy', last);
  });

  return () => { unsubVendor(); unsubShop(); };
}

/**
 * A vendor SALE completing: the ka-ching, straight off the receipt.
 *
 * Why not rely on the credits sound alone: it watches `softBalance`, which
 * only moves when the `economy:updated` socket push arrives. The sell response
 * carries inventory and a vendor balance but does not write `softBalance`, so
 * a sale was silent whenever that push was late or missing, and the sell
 * button's own click is suppressed. Follow-up: a dedicated sell asset; for now
 * this reuses `uiCredits` (the coin sound), which fits.
 */
export function createSellSound(mixer: MixerHandle): () => void {
  const last = { at: -Infinity };
  let lastReceipt: unknown = vendorStateStore.get().lastReceipt;
  return vendorStateStore.subscribe((s) => {
    const receipt = s.lastReceipt;
    if (receipt === lastReceipt) return;
    lastReceipt = receipt;
    if (receipt?.kind !== 'sell') return;
    sellSound.at = performance.now();
    playThrottled(mixer, 'uiCredits', last);
  });
}

/**
 * The error sound's throttle, shared by everything that plays it.
 *
 * Two things sound an error: a store gaining one (below) and an error plate
 * actually appearing on screen (`error-fx.ts`). They are usually the same
 * event seen twice, so they share one retrigger window and the player hears it
 * once.
 */
const errorLast = { at: -Infinity };

/** Clears the shared window. Called when a view mounts, so one view's last error cannot mute the next's. */
export function resetErrorSoundWindow(): void {
  errorLast.at = -Infinity;
}

/** Plays the error sound, no more often than the shared retrigger window. */
export function playErrorSound(mixer: MixerHandle): void {
  playThrottled(mixer, 'uiError', errorLast);
}

/** Any of the game stores reporting a fresh failure. */
export function createErrorSound(mixer: MixerHandle): () => void {
  resetErrorSoundWindow();
  const unsubs = ERROR_STORES.map((store) => {
    // Seeded from the CURRENT value so an error already standing at mount
    // does not announce itself. Narrow false positive, same class as the
    // credits-on-reconnect one: a store that gains an error between this read
    // and the subscription landing would sound once on entry. Not guarded,
    // because the window is a few microseconds of synchronous code and the
    // guard would cost more clarity than the miss is worth.
    let had = store.get().error !== null;
    return store.subscribe((s) => {
      const has = s.error !== null;
      const rising = has && !had;
      had = has;
      if (rising) playErrorSound(mixer);
    });
  });
  return () => { for (const u of unsubs) u(); };
}

/**
 * The pencil, whenever the player picks a DIFFERENT raid zone on the map.
 *
 * Only a change from one zone to another counts. The map seeds a default zone
 * the first time it opens (nothing to a zone): that is the game choosing, not
 * the player, and a pencil scratching as the map opens would be wrong.
 */
export function createPencilSound(mixer: MixerHandle): () => void {
  const last = { at: -Infinity };
  let previous = raidStateStore.get().selectedRegion;
  return raidStateStore.subscribe((s) => {
    const now = s.selectedRegion;
    const before = previous;
    previous = now;
    if (now === null || before === null || now === before) return;
    playThrottled(mixer, 'uiPencil', last);
  });
}

/** All five, pre-loaded. Returns a single teardown. */
export function createUiSounds(mixer: MixerHandle): () => void {
  for (const id of ['uiClick', 'uiCredits', 'uiBuy', 'uiError', 'uiPencil'] as TrackId[]) {
    void mixer.load(TRACKS[id].path).catch(() => { /* retried by the play path */ });
  }
  const unsubs = [
    createClickSound(mixer),
    createCreditsSound(mixer),
    createPurchaseSound(mixer),
    createSellSound(mixer),
    createErrorSound(mixer),
    createPencilSound(mixer),
    createErrorFx(mixer),
  ];
  return () => { for (const u of unsubs) u(); };
}
